import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Avatar,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Circle as UnreadIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI, usersAPI } from '../services/api';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { formatDistanceToNow, format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const safeArray = (val) => Array.isArray(val) ? val : [];

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const userId = user?.userId || user?.attributes?.sub || user?.username;
  const { data: buildingId = null, isLoading: isBuildingLoading } = useQuery(
    ['notificationBuilding', userId],
    async () => {
      const response = await usersAPI.getAssociations(userId);
      const associations = Array.isArray(response.data) ? response.data : [];
      const ids = [...new Set(associations.map(item => item?.buildingId).filter(Boolean))];
      return ids.length === 1 ? ids[0] : null;
    },
    { enabled: Boolean(userId), staleTime: 60000 }
  );

  // Fetch user notifications - only after login and with optimized intervals
  const { data: notifications = [], isLoading, error, refetch } = useQuery(
    ['userNotifications', userId, buildingId],
    () => userId && buildingId ? notificationsAPI.getUserNotifications(userId, undefined, buildingId) : Promise.resolve({ data: [] }),
    {
      select: (response) => response.data || [],
      enabled: !!userId && !!user && !!buildingId && !isBuildingLoading,
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 60000, // Refresh on focus only when data is over one minute old
      cacheTime: 600000, // Keep in cache for 10 minutes
      retry: 1, // Only retry once on failure
      retryDelay: 5000, // Wait 5 seconds before retry
    }
  );

  // Ensure notifications is always an array
  const notificationsSafe = safeArray(notifications);
  const sortedNotifications = [...notificationsSafe].sort((a, b) => (
    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
  ));

  // Mark notification as read mutation
  const markAsReadMutation = useMutation(
    (notificationId) => notificationsAPI.markAsRead(notificationId),
    {
      onSuccess: (_response, notificationId) => {
        queryClient.setQueriesData(['userNotifications', userId], current => {
          if (!Array.isArray(current?.data)) return current;
          return {
            ...current,
            data: current.data.map(item => (
              item.notificationId === notificationId ? { ...item, isRead: true } : item
            ))
          };
        });
        queryClient.invalidateQueries(['userNotifications', userId]);
      },
      onError: error => console.error('Failed to persist notification read state:', error),
    }
  );

  // Don't render notification bell if user is not logged in
  if (!user || !userId) {
    return null;
  }

  // Priority colors
  const priorityColors = {
    LOW: 'success',
    MEDIUM: 'warning', 
    HIGH: 'error',
    URGENT: 'error'
  };

  // Category emojis
  const categoryEmojis = {
    MAINTENANCE: '🔧',
    UTILITY: '⚡',
    FINANCIAL: '💰',
    SECURITY: '🔒',
    COMMUNITY: '👥',
    POLICY: '📋',
    FACILITY: '🏢',
    EMERGENCY: '🚨',
    GENERAL: '📢'
  };

  const unreadCount = notificationsSafe.filter(n => !n.isRead).length;
  const recentNotifications = sortedNotifications.slice(0, 5);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    refetch();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.notificationId);
    }
    navigate('/notifications-dashboard', {
      state: { selectedNotificationId: notification.notificationId }
    });
    handleClose();
  };

  const handleViewAll = () => {
    navigate('/notifications-dashboard');
    handleClose();
  };

  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else {
      return format(date, 'MMM dd, HH:mm');
    }
  };

  const truncateMessage = (message, maxLength = 60) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  if (!userId) {
    return null; // Don't show if user not logged in
  }

  return (
    <>
      <Tooltip title={`${unreadCount} unread notifications`}>
        <IconButton
          size="large"
          aria-label={`notifications ${unreadCount > 0 ? `${unreadCount} unread` : ''}`}
          color="inherit"
          onClick={handleClick}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
        PaperProps={{
          sx: {
            width: 'clamp(320px, 44vw, 420px)',
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: { xs: '64vh', sm: 600 },
            mt: 1,
            borderRadius: 2,
            overflow: 'hidden',
            '& .MuiMenuItem-root': {
              px: 0, // Remove padding to use our custom content
            },
          },
        }}
        MenuListProps={{
          sx: {
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: { xs: '64vh', sm: 600 },
            overflow: 'hidden'
          }
        }}
        marginThreshold={16}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.25 }, borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
          <Typography
            variant="h6"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '1.05rem', sm: '1.25rem' }
            }}
          >
            <NotificationsIcon />
            Notifications
            {unreadCount > 0 && (
              <Chip 
                size="small" 
                label={unreadCount} 
                color="error" 
                sx={{ minWidth: 24, height: 20 }}
              />
            )}
          </Typography>
        </Box>

        {/* Loading state */}
        {isLoading && (
          <MenuItem disabled>
            <Typography color="text.secondary">Loading notifications...</Typography>
          </MenuItem>
        )}

        {/* Error state */}
        {error && (
          <MenuItem disabled>
            <Alert severity="error" sx={{ width: '100%' }}>
              Failed to load notifications
            </Alert>
          </MenuItem>
        )}

        {/* Empty state */}
        {!isLoading && !error && notificationsSafe.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        )}

        {/* Notifications list */}
        {!isLoading && !error && recentNotifications.length > 0 && (
          <>
            <Box sx={{ overflowY: 'auto', overscrollBehavior: 'contain', minHeight: 0 }}>
            {recentNotifications.map((notification, index) => (
              <div key={notification.notificationId}>
                <MenuItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                    alignItems: 'flex-start',
                    bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                    '&:hover': {
                      bgcolor: notification.isRead ? 'action.hover' : 'action.selected',
                    },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: { xs: 38, sm: 40 } }}>
                    <Avatar
                      sx={{
                        width: { xs: 30, sm: 32 },
                        height: { xs: 30, sm: 32 },
                        bgcolor: notification.isRead ? 'grey.300' : priorityColors[notification.priority] + '.light',
                        fontSize: '0.75rem'
                      }}
                    >
                      {categoryEmojis[notification.category] || '📢'}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    sx={{ minWidth: 0, pr: { xs: 0.25, sm: 0.5 } }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: notification.isRead ? 'normal' : 'bold',
                            flex: 1,
                            minWidth: 0,
                            fontSize: { xs: '0.86rem', sm: '0.875rem' },
                            lineHeight: 1.35,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.isRead && (
                          <UnreadIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: { xs: '0.72rem', sm: '0.75rem' },
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {truncateMessage(notification.message)}
                        </Typography>
                        {notification.attachmentUrl && (
                          <Box
                            component="img"
                            src={notification.attachmentUrl}
                            alt={notification.attachment?.fileName || 'Notification attachment'}
                            sx={{ display: 'block', width: 96, height: 56, objectFit: 'cover', borderRadius: 1, mt: 0.75 }}
                          />
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'flex-start', sm: 'space-between' },
                            flexWrap: 'wrap',
                            gap: 1,
                            minWidth: 0
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ flex: { xs: '0 1 auto', sm: 1 }, minWidth: 0, whiteSpace: 'nowrap' }}
                          >
                            {formatNotificationTime(notification.createdAt)}
                          </Typography>
                          <Chip
                            size="small"
                            label={notification.priority}
                            color={priorityColors[notification.priority]}
                            sx={{ height: 20, fontSize: '0.625rem', flexShrink: 0, maxWidth: '100%' }}
                          />
                        </Box>
                      </Box>
                    }
                  />
                </MenuItem>
                {index < recentNotifications.length - 1 && <Divider />}
              </div>
            ))}
            </Box>

            {/* Footer */}
            <Divider />
            <Box sx={{ p: 1, flexShrink: 0, bgcolor: 'background.paper' }}>
              <Button
                fullWidth
                variant="text"
                onClick={handleViewAll}
                sx={{ justifyContent: 'center' }}
              >
                View All Notifications
                <ExpandMoreIcon sx={{ ml: 1 }} />
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
